import type { ReadonlyVec3 } from "gl-matrix";
import { vec3 } from "gl-matrix";

import type { Coordinate } from "features/coordinates/coordinatesSlice";
import { getLabelPixelWidth, getRoundedRectPath } from "features/render/svgUtils";

export type CoordinateLabelSVGStringProps = {
    svgWidth: number;
    labelSVGHeight?: number;
    radius?: number;
    fillColor?: string;
    fillColorPath?: string;
    fontSize?: number;
    formattedPos?: [string, string, string];
};

/**
 * Utility to build the coordinate label SVG markup as a string.
 * Used for overlays that require innerHTML injection.
 */
export function buildCoordinateLabelSVGString({
    svgWidth,
    labelSVGHeight = 16,
    radius = 4,
    fillColor = "#FFFFFF",
    fillColorPath = "#000000ff",
    fontSize = 12,
    formattedPos = ["0", "0", "0"],
}: CoordinateLabelSVGStringProps) {
    const space = 3;
    return `
        <svg width="${svgWidth}" height="${labelSVGHeight}" style="overflow:visible;">
            <path d="${getRoundedRectPath(svgWidth, labelSVGHeight, radius)}" fill="${fillColorPath}" />
            <text
                x="${svgWidth * 0.5}"
                y="${labelSVGHeight * 0.5 + fontSize * 0.5 - 2}"
                fill="${fillColor}"
                font-family="Inter, sans-serif"
                font-size="${fontSize}"
                text-anchor="middle"
            >
                <tspan font-weight="600">X:</tspan>
                <tspan dx="${space}">${formattedPos[0]}</tspan>
                <tspan dx="${space}"> </tspan>
                <tspan font-weight="600">Y:</tspan>
                <tspan dx="${space}">${formattedPos[1]}</tspan>
                <tspan dx="${space}"> </tspan>
                <tspan font-weight="600">Z:</tspan>
                <tspan dx="${space}">${formattedPos[2]}</tspan>
            </text>
        </svg>
    `;
}

export function computeLabelWidth(labelText: string, fontSize = 12, paddingX = 4): number {
    const fontPixelSizeFactor =
        typeof document !== "undefined" && document.fonts && document.fonts.check("12px Inter") ? 0.52 : 0.55;
    const textWidth = getLabelPixelWidth(labelText, fontSize, 0, fontPixelSizeFactor);
    return textWidth + paddingX * 2;
}

export function computeLabelPos({
    normalVS,
    coord,
    formattedPos,
    initialX,
    initialY,
    labelSVGHeight = 16,
}: {
    normalVS: ReadonlyVec3;
    coord: Coordinate;
    formattedPos?: [string, string, string];
    initialX: number;
    initialY: number;
    labelSVGHeight?: number;
}) {
    const isSurface = coord.point.snapType === "normal";
    const separation = 45; // px, distance from icon to label
    if (!isSurface) {
        return { x: initialX, y: initialY - separation, positionOnTheSide: false };
    }
    const [xVal, yVal, zVal] = formattedPos ?? coord.point.position;
    const fontSize = 12;
    const labelText = `X: ${xVal} Y: ${yVal} Z: ${zVal}`;
    const svgWidth = computeLabelWidth(labelText, fontSize);
    const screenOffset = coord.screenOffset ?? { x: 0, y: 0 };
    const projectionToHorizontalAxis = vec3.dot(normalVS, [1, 0, 0]);
    const projectionToVerticalAxis = vec3.dot(normalVS, [0, 1, 0]);
    const projectionThreshold = 0.8; // Threshold for projection to determine label position
    if (projectionToVerticalAxis < -projectionThreshold) {
        // Normal points down (camera Y-)
        return { x: initialX, y: initialY + separation - labelSVGHeight, positionOnTheSide: false };
    } else if (projectionToHorizontalAxis > projectionThreshold) {
        // Normal points right (camera X+)
        return {
            x: initialX + svgWidth * 0.5 - separation - screenOffset.x,
            y: initialY - labelSVGHeight * 0.5,
            positionOnTheSide: true,
        };
    } else if (projectionToHorizontalAxis < -projectionThreshold) {
        // Normal points left (camera X-)
        return {
            x: initialX - svgWidth * 0.5 + separation - screenOffset.x,
            y: initialY - labelSVGHeight * 0.5,
            positionOnTheSide: true,
        };
    } else {
        // Fallback to default position in case of ambiguous normal direction or just normal is "vertical"
        return { x: initialX, y: initialY - separation, positionOnTheSide: false };
    }
}

export function updateTempCoordinateLabel(
    {
        x,
        y,
        position,
        normalVS,
        screenOffset,
        formattedPos = ["0", "0", "0"],
        isSurface = true,
    }: {
        x: number;
        y: number;
        position: [number, number, number] | undefined;
        normalVS: ReadonlyVec3 | undefined;
        screenOffset: { x: number; y: number } | undefined;
        formattedPos?: [string, string, string];
        isSurface?: boolean;
    },
    svg: SVGSVGElement,
) {
    const tempCoordContainer = svg.children.namedItem("temp-cursor-coordinate-label-overlay") as HTMLElement;

    if (!tempCoordContainer) {
        return;
    }
    const labelSVGHeight = 16;
    const radius = 4;
    const fillColorPath = "#0000004D";
    const fillColor = "#FFFFFF";

    const [xVal, yVal, zVal] = position ?? [0, 0, 0];
    const fontSize = 12;
    const labelText = `X: ${formattedPos ? formattedPos[0] : xVal.toFixed(3)} Y: ${formattedPos ? formattedPos[1] : yVal.toFixed(3)} Z: ${formattedPos ? formattedPos[2] : zVal.toFixed(3)}`;

    const svgWidth = computeLabelWidth(labelText, fontSize);
    tempCoordContainer.innerHTML = buildCoordinateLabelSVGString({
        svgWidth,
        labelSVGHeight,
        radius,
        fillColor,
        fillColorPath,
        formattedPos: formattedPos,
    });
    x = x - svgWidth * 0.5;
    if (normalVS !== undefined && isSurface) {
        const labelPos = computeLabelPos({
            normalVS,
            coord: {
                id: "temp-cursor-coordinate-label-overlay",
                screenOffset: screenOffset || { x: 0, y: 0 },
                point: {
                    position: [xVal, yVal, zVal],
                },
            },
            initialX: x,
            initialY: y,
            labelSVGHeight,
        });
        tempCoordContainer.style.transform = `translate(${labelPos.x}px, ${labelPos.y}px)`;
    } else {
        tempCoordContainer.style.transform = `translate(${x}px, ${y - 45}px)`;
    }
    tempCoordContainer.style.display = "block";
}

export function clearTempCoordinatesLabel(svg: SVGSVGElement) {
    const tempCoordContainer = svg.children.namedItem("temp-cursor-coordinate-label-overlay");
    if (tempCoordContainer) {
        const tempCoordHtml = tempCoordContainer as HTMLElement;
        tempCoordHtml.innerHTML = "";
        tempCoordHtml.style.display = "none";
    }
}
